import React, { useState, useEffect } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography, IconButton, Autocomplete, TextField, Avatar, Chip } from "@mui/material";
import { Cancel, Save, Fullscreen, FullscreenExit, ErrorOutline } from "@mui/icons-material";
import { WhatsAppTemplate, TelegramTemplate, WhatsAppBusinessTemplate, GSMTextTemplate } from 'components/TemplateEditor';
import 'react-quill/dist/quill.snow.css';
import { Template } from "modules/Interfaces/InstanUpdates/Template";
import { DataSets } from "modules/Interfaces/InstanUpdates";
import { Logger } from "utils/Logger";
import { apiClient } from "utils/ApiClient";

interface TemplateDialogProps {
    dialogOpen: boolean;
    closeDialog: () => void;
    isEditing: boolean;
    currentTemplate: Partial<Template> | null;
    setCurrentTemplate: React.Dispatch<React.SetStateAction<Partial<Template> | null>>;
    saveTemplate: () => Promise<void>;
    dataSets: DataSets[];
    templateSchema: any; // Using 'any' as per the static schema definition
    templateNameError: string; // New prop for templateName validation error
    setTemplateNameError: (error: string) => void; // New prop to set templateName error
    companyId: string; // Add companyId to props
    saveError?: string;
    setSaveError?: (error: string) => void;
    preselectedSystemTemplateId?: string | null;
}

export const TemplateDialog: React.FC<TemplateDialogProps> = ({
    dialogOpen,
    closeDialog,
    isEditing,
    currentTemplate,
    setCurrentTemplate,
    saveTemplate,
    // templateTypeOptions,
    dataSets,
    templateSchema,
    templateNameError, // Destructure new prop
    setTemplateNameError, // Destructure new prop
    companyId, // Destructure new prop
    saveError,
    setSaveError,
    preselectedSystemTemplateId,
}) => {
    const [isMaximized, setIsMaximized] = useState(false);
    const isEmail = currentTemplate?.templateType === 'mail';
    const excludedFields = ['_id', 'companyId', '__v', 'templateCreatedByUserId', 'templateUpdatedByUserId', 'templateUpdatedOn', 'createdAt', 'updatedAt', 'sharedTo'];

    const [availableColumns, setAvailableColumns] = useState<string[]>([]);
    const [loadingColumns, setLoadingColumns] = useState<boolean>(false);
    const [lastFetchedDataSet, setLastFetchedDataSet] = useState<string>('');

    const [copyFromOptions, setCopyFromOptions] = useState<any[]>([]);
    const [selectedSystemTemplate, setSelectedSystemTemplate] = useState<any>(null);

    useEffect(() => {
        if (dialogOpen && !isEditing && currentTemplate?.templateType) {
            const fetchCopyOptions = async () => {
                try {
                    const [sysRes, userRes] = await Promise.all([
                        apiClient.get(`/iu/systemtemplate/catalog/${currentTemplate.templateType}`).catch(() => ({ data: [] })),
                        apiClient.get(`/iu/contenttemplate/company/${currentTemplate.templateType}`).catch(() => ({ data: [] })),
                    ]);
                    const sysTemplates = (Array.isArray(sysRes.data) ? sysRes.data : []).map((t: any) => ({ ...t, _source: 'system' }));
                    const templateType = (currentTemplate.templateType || '').toLowerCase();
                    const requiresApproval = templateType === 'mail' || templateType === 'sms';
                    // Mail/SMS: only approved templates may be copy sources. WhatsApp and other types have no approval workflow.
                    const userTemplates = (Array.isArray(userRes.data) ? userRes.data : [])
                        .filter((t: any) => !requiresApproval || t.isApproved === true)
                        .map((t: any) => ({ ...t, _source: 'user' }));
                    const merged = [...sysTemplates, ...userTemplates];
                    setCopyFromOptions(merged);
                    if (preselectedSystemTemplateId) {
                        const match = merged.find((t: any) => t._id === preselectedSystemTemplateId);
                        if (match) {
                            setSelectedSystemTemplate(match);
                            const isSms = currentTemplate?.templateType === 'sms' || currentTemplate?.templateType === 'gsmText';
                            const fromSys = match._source === 'system' && match.templateName;
                            const fromUsr = match._source === 'user' && match._id;
                            setCurrentTemplate((prev) => prev ? {
                                ...prev,
                                templateContent: isSms ? '' : (match.templateContent != null ? String(match.templateContent) : (prev.templateContent || '')),
                                templateId: match.templateId || undefined,
                                category: match.category || undefined,
                                keywords: match.keywords?.length ? [...match.keywords] : undefined,
                                ...(match.collectionId ? { collectionId: match.collectionId } : {}),
                                ...(fromSys
                                    ? { smsCopiedFromSystemTemplateName: String(match.templateName), copiedFromApprovedUserTemplateId: undefined }
                                    : fromUsr
                                        ? { copiedFromApprovedUserTemplateId: String(match._id), smsCopiedFromSystemTemplateName: undefined }
                                        : { smsCopiedFromSystemTemplateName: undefined, copiedFromApprovedUserTemplateId: undefined }),
                                ...(match.whatsappBusinessConfig ? { whatsappBusinessConfig: { ...match.whatsappBusinessConfig } } : {}),
                            } : null);
                            return;
                        }
                    }
                    setSelectedSystemTemplate(null);
                } catch {
                    setCopyFromOptions([]);
                    setSelectedSystemTemplate(null);
                }
            };
            fetchCopyOptions();
        } else if (!dialogOpen) {
            setCopyFromOptions([]);
            setSelectedSystemTemplate(null);
        }
    }, [dialogOpen, isEditing, currentTemplate?.templateType, preselectedSystemTemplateId]);

    useEffect(() => {
        if (!dialogOpen && setSaveError) {
            setSaveError('');
        }
    }, [dialogOpen, setSaveError]);

    const getTemplateDisplayName = (name: string) => {
        if (!name) return '';
        const lower = name.toLowerCase();
        if (lower.startsWith('iust-')) return name.substring(5);
        if (lower.startsWith('iut-')) {
            const parts = name.split('-');
            return parts.length >= 3 ? parts.slice(2).join('-') : name;
        }
        return name;
    };

    const handleSystemTemplateChange = (_: any, value: any) => {
        if (!value || value._id === '__none__') {
            setSelectedSystemTemplate(null);
            setCurrentTemplate((prev) => prev ? {
                ...prev,
                templateContent: '',
                templateId: undefined,
                category: undefined,
                keywords: undefined,
                smsCopiedFromSystemTemplateName: undefined,
                copiedFromApprovedUserTemplateId: undefined,
            } : null);
            return;
        }
        setSelectedSystemTemplate(value);
        const fromSystem = value._source === 'system' && value.templateName;
        const fromUser = value._source === 'user' && value._id;
        const isSmsType = currentTemplate?.templateType === 'sms' || currentTemplate?.templateType === 'gsmText';
        setCurrentTemplate((prev) => prev ? {
            ...prev,
            templateContent: isSmsType ? '' : (value.templateContent != null ? String(value.templateContent) : ''),
            templateId: value.templateId || undefined,
            category: value.category || undefined,
            keywords: value.keywords?.length ? [...value.keywords] : undefined,
            ...(value.collectionId ? { collectionId: value.collectionId } : {}),
            ...(fromSystem
                ? { smsCopiedFromSystemTemplateName: String(value.templateName), copiedFromApprovedUserTemplateId: undefined }
                : fromUser
                    ? { copiedFromApprovedUserTemplateId: String(value._id), smsCopiedFromSystemTemplateName: undefined }
                    : { smsCopiedFromSystemTemplateName: undefined, copiedFromApprovedUserTemplateId: undefined }),
            ...(value.whatsappBusinessConfig ? { whatsappBusinessConfig: { ...value.whatsappBusinessConfig } } : {}),
        } : null);
    };

    // Editor-specific configs live within child editors now

    // Removed unused getDisplayTemplateName helper

    // Helper function to remove prefix from collectionName for display
    const getDisplayCollectionName = (fullCollectionName: string | undefined) => {
        if (!fullCollectionName) return "";
        // Assuming format is "IU-<companyId>-CollectionName"
        const parts = fullCollectionName.split('-');
        if (parts.length >= 3 && parts[0] === 'IU' && parts[1] === companyId) {
            return parts.slice(2).join('-'); // Return everything after "IU-<companyId>-"
        }
        return fullCollectionName; // Fallback
    };

    // Function to fetch dataset schema and extract column names
    const fetchDataSetColumns = async (dataSetId: string) => {
        if (!dataSetId) {
            setAvailableColumns([]);
            setLastFetchedDataSet('');
            return;
        }

        // Prevent duplicate API calls for the same dataset
        if (lastFetchedDataSet === dataSetId) {
            return;
        }

        try {
            setLoadingColumns(true);
            const selectedDataSet = dataSets.find(ds => ds._id === dataSetId);
            if (!selectedDataSet) {
                setAvailableColumns([]);
                setLastFetchedDataSet('');
                return;
            }

            const fullCollectionName = selectedDataSet.collectionName;
            const parts = fullCollectionName.split('-');
            const baseCollectionName = parts.length >= 3 ? parts.slice(2).join('-') : fullCollectionName;

            Logger.debug('TemplateDialog.tsx', `Fetching schema for dataset: ${dataSetId}, full collection: ${fullCollectionName}, base collection: ${baseCollectionName}`);
            
            // Use ApiClient instead of fetch (automatically includes Authorization header)
            const data = await apiClient.get(`/iu/ds/cmp/set/entry/schema/${encodeURIComponent(baseCollectionName)}`);
            const schema = data.data;
            
            // Extract available columns (excluding system fields)
            const columns = Object.keys(schema.paths || {}).filter(key => 
                !['_id', 'createdAt', 'updatedAt', '__v'].includes(key)
            );
            setAvailableColumns(columns);
            setLastFetchedDataSet(dataSetId);
            Logger.debug('TemplateDialog.tsx', `Available columns: ${columns.join(', ')}`);
        } catch (error) {
            Logger.error('TemplateDialog.tsx', `Failed to fetch dataset schema: ${error}`);
            setAvailableColumns([]);
            setLastFetchedDataSet('');
        } finally {
            setLoadingColumns(false);
        }
    };

    // Fetch columns when dataset changes
    useEffect(() => {
        if (dialogOpen && currentTemplate?.collectionId) {
            fetchDataSetColumns(currentTemplate.collectionId);
        } else {
            setAvailableColumns([]);
        }
    }, [dialogOpen, currentTemplate?.collectionId, dataSets]);

    // Removed local media upload/render helpers; handled within specific editors if needed

    // Handle change for text fields with validation
    const handleTextFieldChange = (key: keyof Template, value: string) => {
        setCurrentTemplate((prev) => prev ? { ...prev, [key]: value } : null);
    };

    const isSaveDisabled = () => {
        if (!currentTemplate) return true;
        if (templateNameError) return true; // Disable if templateName has error

        // Check required fields based on schema, if schema provides 'isRequired'
        const requiredFields = (Object.keys(templateSchema.paths) as (keyof typeof templateSchema.paths)[])
                                .filter(key => templateSchema.paths[key]?.isRequired && !excludedFields.includes(key as string));
        
        for (const field of requiredFields) {
            // Special handling for templateContent, as it might be an empty string but still "required"
            if (field === "templateContent") {
                if (typeof currentTemplate[field] !== 'string' || currentTemplate[field].trim() === '') {
                    return true;
                }
            } else if (!currentTemplate[field as keyof Template]) {
                return true;
            }
        }

        // Additional validation for email templates
        if (currentTemplate.templateType === 'mail') {
            // Check if email subject is provided and not empty
            if (!currentTemplate.emailSubject || currentTemplate.emailSubject.trim() === '') {
                return true;
            }
        }

        return false;
    };


    return (
        <Dialog 
            open={dialogOpen} 
            onClose={closeDialog} 
            fullWidth 
            maxWidth={(isEmail || isMaximized) ? false : "md"}
            fullScreen={isEmail || isMaximized}
            sx={{
                '& .MuiDialog-paper': {
                    margin: (isEmail || isMaximized) ? 0 : 'auto',
                    maxWidth: (isEmail || isMaximized) ? '100vw' : 'md',
                    maxHeight: (isEmail || isMaximized) ? '100vh' : '90vh',
                    width: (isEmail || isMaximized) ? '100vw' : '100%',
                    height: (isEmail || isMaximized) ? '100vh' : '90vh',
                    overflow: 'hidden',
                }
            }}
        >
            <DialogTitle sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                pr: 1
            }}>
                <Typography component="span" variant="h6">
                    {isEditing ? `Edit ${currentTemplate?.templateType?.toUpperCase()} Template` : `Create New ${currentTemplate?.templateType?.toUpperCase()} Template`}
                </Typography>
                {!isEmail && (
                    <IconButton
                        onClick={() => setIsMaximized(!isMaximized)}
                        size="small"
                        sx={{ ml: 1 }}
                    >
                        {isMaximized ? <FullscreenExit /> : <Fullscreen />}
                    </IconButton>
                )}
            </DialogTitle>
            <DialogContent sx={{ 
                flex: 1,
                minHeight: 0,
                overflow: 'auto',
                display: 'flex',
                flexDirection: 'column'
            }}>
                {/* Save Error Display */}
                {saveError && (
                    <Box sx={{ mb: 2 }}>
                        <Typography 
                            variant="body2" 
                            color="error" 
                            sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: 1,
                                backgroundColor: 'rgba(244, 67, 54, 0.1)',
                                padding: 2,
                                borderRadius: 1,
                                border: '1px solid rgba(244, 67, 54, 0.3)'
                            }}
                        >
                            <ErrorOutline sx={{ fontSize: 20 }} />
                            {saveError}
                        </Typography>
                    </Box>
                )}
                
                {!isEditing && currentTemplate && copyFromOptions.length > 0 && (
                    <Box sx={{ mt: 2, mb: 2 }}>
                        <Autocomplete
                            size="small"
                            options={[{ _id: '__none__', templateName: 'None', _source: '' }, ...copyFromOptions]}
                            groupBy={(option: any) => option._id === '__none__' ? '' : option._source === 'system' ? 'System Templates' : 'My Templates'}
                            getOptionLabel={(option: any) =>
                                option._id === '__none__' ? 'None' : getTemplateDisplayName(option.templateName)
                            }
                            value={selectedSystemTemplate}
                            onChange={handleSystemTemplateChange}
                            isOptionEqualToValue={(opt: any, val: any) => opt._id === val?._id}
                            renderOption={(props, option: any) => (
                                <li {...props} key={option._id}>
                                    <Box display="flex" alignItems="center" gap={1.5} py={0.5} width="100%">
                                        {option._id !== '__none__' && option.thumbnailUrl ? (
                                            <Box
                                                component="img"
                                                src={option.thumbnailUrl}
                                                sx={{ width: 60, height: 60, borderRadius: 1, objectFit: 'cover', flexShrink: 0, border: '1px solid', borderColor: 'divider' }}
                                            />
                                        ) : option._id !== '__none__' ? (
                                            <Avatar variant="rounded" sx={{ width: 60, height: 60, fontSize: '0.7rem', bgcolor: 'action.selected' }}>
                                                N/A
                                            </Avatar>
                                        ) : null}
                                        <Typography variant="body2" sx={{ flex: 1 }}>
                                            {option._id === '__none__' ? 'None' : getTemplateDisplayName(option.templateName)}
                                        </Typography>
                                        {option._id !== '__none__' && (
                                            <Chip
                                                label={option._source === 'system' ? 'System' : 'User'}
                                                size="small"
                                                color={option._source === 'system' ? 'info' : 'success'}
                                                sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700 }}
                                            />
                                        )}
                                    </Box>
                                </li>
                            )}
                            renderInput={(params) => <TextField {...params} label="Copy from Template" />}
                        />
                        {selectedSystemTemplate?.thumbnailUrl && (
                            <Box sx={{ mt: 1.5, textAlign: 'center' }}>
                                <Box
                                    component="img"
                                    src={selectedSystemTemplate.thumbnailUrl}
                                    alt={getTemplateDisplayName(selectedSystemTemplate.templateName)}
                                    sx={{
                                        maxWidth: '100%',
                                        maxHeight: 200,
                                        borderRadius: 1,
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        objectFit: 'contain',
                                    }}
                                />
                            </Box>
                        )}
                    </Box>
                )}

                {currentTemplate && currentTemplate.templateType === 'whatsapp' && (
                    <WhatsAppTemplate
                        currentTemplate={currentTemplate}
                        setCurrentTemplate={setCurrentTemplate as any}
                        dataSets={dataSets}
                        templateNameError={templateNameError}
                        setTemplateNameError={setTemplateNameError}
                        companyId={companyId}
                        getDisplayCollectionName={getDisplayCollectionName}
                        onSaveError={setSaveError || (() => {})}
                        isEditing={isEditing}
                        dialogOpen={dialogOpen}
                    />
                )}
                {currentTemplate && currentTemplate.templateType !== 'mail' && currentTemplate.templateType !== 'whatsapp' && (
                    currentTemplate.templateType === 'telegram' ? (
                        <TelegramTemplate
                            currentTemplate={currentTemplate}
                            setCurrentTemplate={setCurrentTemplate as any}
                            dataSets={dataSets}
                            templateNameError={templateNameError}
                            setTemplateNameError={setTemplateNameError}
                            companyId={companyId}
                            getDisplayCollectionName={getDisplayCollectionName}
                            onSaveError={setSaveError || (() => {})}
                            isEditing={isEditing}
                        />
                    ) : currentTemplate.templateType === 'whatsappbusiness' ? (
                        <WhatsAppBusinessTemplate
                            currentTemplate={currentTemplate}
                            setCurrentTemplate={setCurrentTemplate as any}
                            dataSets={dataSets}
                            templateNameError={templateNameError}
                            setTemplateNameError={setTemplateNameError}
                            companyId={companyId}
                            getDisplayCollectionName={getDisplayCollectionName}
                            onSaveError={setSaveError || (() => {})}
                            isEditing={isEditing}
                        />
                    ) : (currentTemplate.templateType === 'sms' || currentTemplate.templateType === 'gsmText') ? (
                        <GSMTextTemplate
                            dialogOpen={dialogOpen}
                            copyFromSelectionKey={selectedSystemTemplate?._id ?? '__none__'}
                            selectedCopyTemplate={!isEditing ? selectedSystemTemplate : null}
                            currentTemplate={currentTemplate}
                            setCurrentTemplate={setCurrentTemplate as any}
                            dataSets={dataSets}
                            templateNameError={templateNameError}
                            setTemplateNameError={setTemplateNameError}
                            companyId={companyId}
                            getDisplayCollectionName={getDisplayCollectionName}
                        />
                    ) : (
                        <Box>Editor for {String(currentTemplate.templateType)} coming soon.</Box>
                    )
                )}
            </DialogContent>
            <DialogActions>
                <Button variant="outlined" color="secondary" onClick={closeDialog} startIcon={<Cancel />}>
                    Cancel
                </Button>
                <Button onClick={saveTemplate} startIcon={<Save />} variant="contained" color="success" disabled={isSaveDisabled()}>
                    Save
                </Button>
            </DialogActions>
            {/* AI dialog removed */}
        </Dialog>
    );
};
